// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Alert, AppIcon, Spinner, Dropdown, TablePagination, Button } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import { adminApi } from '../api/adminApi';
import type {
  RelationshipAssignmentHistory,
  AssignmentType,
  PageResponse,
  AdvisorWithAdmin,
} from '../types/admin';
import { useAdmin } from '../contexts/AdminContext';

const AssignmentHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdmin();

  const [history, setHistory] = useState<RelationshipAssignmentHistory[]>([]);
  const [advisors, setAdvisors] = useState<AdvisorWithAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Filters
  const [advisorFilter, setAdvisorFilter] = useState<string>('');

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response: PageResponse<RelationshipAssignmentHistory> =
        await adminApi.getAssignmentHistory(
          page,
          pageSize,
          undefined,
          advisorFilter || undefined
        );

      setHistory(response.content);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Failed to load assignment history:', err);
      setError('Failed to load assignment history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, advisorFilter]);

  const loadAdvisors = useCallback(async () => {
    try {
      const response = await adminApi.listAdvisors({}, 0, 100);
      setAdvisors(response.content);
    } catch (err) {
      console.error('Failed to load advisors:', err);
    }
  }, []);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadAdvisors();
    } else if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [adminLoading, isAdmin, loadAdvisors, navigate]);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadHistory();
    }
  }, [adminLoading, isAdmin, loadHistory]);

  const getAssignmentTypeLabel = (type: AssignmentType): { text: string; color: 'green' | 'blue' | 'orange' | 'purple' } => {
    switch (type) {
      case 'INITIAL_ASSIGNMENT':
        return { text: 'Initial', color: 'green' };
      case 'MANUAL_REASSIGNMENT':
        return { text: 'Manual', color: 'blue' };
      case 'DEACTIVATION_REASSIGNMENT':
        return { text: 'Deactivation', color: 'orange' };
      case 'BULK_REASSIGNMENT':
        return { text: 'Bulk', color: 'purple' };
      default:
        return { text: type, color: 'blue' };
    }
  };

  const advisorOptions = [
    { key: 'all', text: 'All Advisors', value: '' },
    ...advisors.map((advisor) => ({
      key: advisor.id,
      text: `${advisor.firstName} ${advisor.lastName}`,
      value: advisor.id,
    })),
  ];

  if (adminLoading || (loading && history.length === 0)) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert variant="error" header="Access Denied">
          <p>You do not have permission to view assignment history.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <AppIcon name="history" />
        <h1 style={{ margin: 0 }}>Assignment History</h1>
      </div>
      <p style={{ color: '#666', marginBottom: '1rem' }}>Track relationship reassignments across your brokerage</p>

      <Button variant="outlined" onClick={() => navigate('/admin/dashboard')} style={{ marginBottom: '1rem' }}>
        <AppIcon name="arrow left" />
        Back to Dashboard
      </Button>

      {error && (
        <Alert variant="error" header="Error" dismissible onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Alert>
      )}

      {/* Filters */}
      <div style={{ padding: '1rem', background: '#f9f9f9', borderRadius: '4px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span>Filter by advisor:</span>
          <div style={{ minWidth: '250px' }}>
            <Dropdown
              placeholder="Select advisor..."
              options={advisorOptions.map(o => ({ value: String(o.value), text: o.text }))}
              value={advisorFilter}
              onChange={(value) => {
                setAdvisorFilter(value);
                setPage(0);
              }}
            />
          </div>
          <Button variant="outlined" onClick={loadHistory}>
            <AppIcon name="refresh" />
          </Button>
        </div>
      </div>

      {/* History Table */}
      <table className="fin-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Relationship</th>
            <th>From</th>
            <th>To</th>
            <th>Type</th>
            <th>Reason</th>
            <th>Assigned By</th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry) => {
            const typeInfo = getAssignmentTypeLabel(entry.assignmentType);
            return (
              <tr key={entry.id}>
                <td>{formatDateTime(entry.assignedAt)}</td>
                <td>
                  <Link to={`/relationships/${entry.profileId}`}>
                    {entry.profileName}
                  </Link>
                </td>
                <td>
                  {entry.fromAdvisorName || (
                    <span style={{ color: 'grey' }}>—</span>
                  )}
                </td>
                <td>{entry.toAdvisorName}</td>
                <td>
                  <span className={`fin-badge fin-badge--${typeInfo.color}`}>
                    {typeInfo.text}
                  </span>
                </td>
                <td>
                  {entry.reason || <span style={{ color: 'grey' }}>—</span>}
                </td>
                <td>{entry.assignedBy}</td>
              </tr>
            );
          })}
          {history.length === 0 && (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center' }}>
                No assignment history found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <TablePagination
          config={{
            currentPage: page + 1,
            totalPages,
            itemsPerPage: pageSize,
            itemsPerPageOptions: [10, 20, 50],
            onPageChange: (next) => setPage(next - 1),
            onItemsPerPageChange: (size) => {
              setPageSize(size);
              setPage(0);
            },
          }}
        />
      )}
    </div>
  );
};

export default AssignmentHistoryPage;
